import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

import { supabase } from '@/lib/supabaseClient';
import { useCommissionCostingEnabled } from '@/hooks/useCommissionCostingEnabled';
import {
  COMMISSION_PERMISSION_COPY,
  CONCENTRATION_WARNING,
  concentratesAllFive,
  moneyPermissionsHeld,
} from '@/lib/commissionPermissions';

/**
 * COM-R.170 — what handing somebody this role lets them do with money.
 *
 * ⚠️ THIS EXISTS BECAUSE THE COPY IT RENDERS WAS ASSERTED IN TESTS AND SHOWN ON
 * NO SCREEN.
 *
 * `COMMISSION_PERMISSION_COPY` and `CONCENTRATION_WARNING` were written by
 * COM-3.9 for a role-permission editor that was never built, and
 * `commissionPermissions.ts` still describes that copy as "the only place the
 * product explains that approving and paying are different authorities". It was
 * not a place at all: nothing imported it, so the product explained nothing
 * while its tests read as proof that it did. Found on the first sign-in of the
 * unprivileged rep login on 2026-08-27.
 *
 * The role picker on the user form is the surface that does exist, and it is
 * where the separation of duties actually stops being theoretical: the moment a
 * role is handed to a person.
 *
 * ⚠️ AN UNREADABLE ANSWER IS SAID OUT LOUD, NOT RENDERED AS "NONE".
 *
 * `role_permissions` is behind RLS. A caller who may not read it gets zero rows,
 * which is indistinguishable from a role that grants no money permission — and
 * the wrong reading of the two is the reassuring one. An empty result is not a
 * fact until you know you were allowed to ask, so a failure says so and a
 * genuinely empty answer renders nothing at all.
 */
export function CommissionRolePermissionsNote({ roleId }: { roleId: string | null | undefined }) {
  const { enabled, loading: gateLoading } = useCommissionCostingEnabled();
  const [held, setHeld] = useState<string[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const id = Number(roleId);

    if (!roleId || !Number.isFinite(id)) {
      setHeld(null);
      setFailed(false);
      return;
    }

    setHeld(null);
    setFailed(false);

    void (async () => {
      const { data, error } = await supabase
        .from('role_permissions')
        .select('permissions!inner ( name )')
        .eq('role_id', id);

      if (cancelled) return;

      if (error) {
        setFailed(true);
        return;
      }

      setHeld(
        (data ?? []).flatMap((row: any) => {
          const name = Array.isArray(row.permissions)
            ? row.permissions[0]?.name
            : row.permissions?.name;
          return typeof name === 'string' && name !== '' ? [name] : [];
        }),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [roleId]);

  // An account not running commission has no use for any of this.
  if (gateLoading || !enabled || !roleId) return null;

  if (failed) {
    return (
      <p className="text-sm text-muted-foreground">
        Could not read what this role grants for commission, so nothing is claimed about it here.
      </p>
    );
  }

  if (held === null) return null;

  const money = moneyPermissionsHeld(held);
  if (money.length === 0) return null;

  return (
    <div className="rounded-md border border-border bg-muted/40 p-3 space-y-2">
      <p className="text-sm font-medium text-foreground">
        This role can act on commission
      </p>
      <ul className="space-y-1.5">
        {money.map((p) => (
          <li key={p} className="text-sm">
            <span className="font-medium text-foreground">
              {COMMISSION_PERMISSION_COPY[p].label}
            </span>
            <span className="text-muted-foreground">
              {' — '}
              {COMMISSION_PERMISSION_COPY[p].explanation}
            </span>
          </li>
        ))}
      </ul>
      {concentratesAllFive(held) && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-2">
          <p className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-500">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
            <span>{CONCENTRATION_WARNING}</span>
          </p>
        </div>
      )}
    </div>
  );
}
